import type {
  ChoiceAnswer,
  DragMatchingImageAnswer,
  DragMatchingTextAnswer,
  FillBlanksAnswer,
  OddOutAnswer,
  OrderAnswer,
  QuestionAnswer,
  SlideElement,
  SpeakingAnswer,
  SpellingAnswer,
} from "@ai-teach/types";
import { AudioPlayerRenderer } from "./renderers/AudioPlayerRenderer";
import { ImageRenderer } from "./renderers/ImageRenderer";
import { PhonicRenderer } from "./renderers/PhonicRenderer";
import { ShapeRenderer } from "./renderers/ShapeRenderer";
import { TextRenderer } from "./renderers/TextRenderer";
import { VideoRenderer } from "./renderers/VideoRenderer";
import { AnswerImageRenderer } from "./renderers/questions/AnswerImageRenderer";
import { CompositionRenderer } from "./renderers/questions/CompositionRenderer";
import { DragMatchingImageRenderer } from "./renderers/questions/DragMatchingImageRenderer";
import { DragMatchingTextRenderer } from "./renderers/questions/DragMatchingTextRenderer";
import { FillBlanksRenderer } from "./renderers/questions/FillBlanksRenderer";
import { ImageChoiceRenderer } from "./renderers/questions/ImageChoiceRenderer";
import { OddImageOutRenderer } from "./renderers/questions/OddImageOutRenderer";
import { OddTextOutRenderer } from "./renderers/questions/OddTextOutRenderer";
import { OrderImageRenderer } from "./renderers/questions/OrderImageRenderer";
import { OrderTextRenderer } from "./renderers/questions/OrderTextRenderer";
import { SpeakingRenderer } from "./renderers/questions/SpeakingRenderer";
import { SpellingRenderer } from "./renderers/questions/SpellingRenderer";
import { TextChoiceRenderer } from "./renderers/questions/TextChoiceRenderer";

interface StaticElementProps {
  element: SlideElement;
  /** What the student answered, when this element is being replayed from a stored play. */
  answer?: QuestionAnswer | undefined;
  /** Every question is over: the correct answer is revealed even where the student never picked. */
  isFrozen?: boolean | undefined;
  /** Ids the student actually answered — `${elementId}-${blankIndex}` for a fillBlanks blank. */
  frozenBlanks?: Set<string> | undefined;
  /** Canvas the elements are drawn on, for a render outside the editor's own canvas. */
  canvasHeight?: number | undefined;
}

export function StaticElement({
  element,
  answer,
  isFrozen,
  frozenBlanks,
  canvasHeight,
}: StaticElementProps) {
  const renderElement = () => {
    switch (element.type) {
      case "text":
        return <TextRenderer element={element} isLiveMode={true} />;
      case "shape":
        return <ShapeRenderer element={element} isLiveMode={true} />;
      case "image":
        return <ImageRenderer element={element} isLiveMode={true} />;
      case "video":
        return <VideoRenderer element={element} isLiveMode={true} />;
      case "audioPlayer":
        return <AudioPlayerRenderer element={element} isLiveMode={true} />;
      case "textChoice":
        return (
          <TextChoiceRenderer
            element={element}
            isLiveMode={true}
            isPicked={(answer as ChoiceAnswer | undefined)?.selectedIds?.includes(element.id)}
            isChoiceFrozen={frozenBlanks?.has(element.id)}
            isFrozen={isFrozen}
          />
        );
      case "imageChoice":
        return (
          <ImageChoiceRenderer
            element={element}
            isLiveMode={true}
            isPicked={(answer as ChoiceAnswer | undefined)?.selectedIds?.includes(element.id)}
            isChoiceFrozen={frozenBlanks?.has(element.id)}
            isFrozen={isFrozen}
          />
        );
      case "dragMatchingText":
        return (
          <DragMatchingTextRenderer
            element={element}
            isLiveMode={true}
            selectedTextId={(answer as DragMatchingTextAnswer | undefined)?.matches?.image}
            isFrozen={isFrozen}
          />
        );
      case "dragMatchingImage":
        return (
          <DragMatchingImageRenderer
            element={element}
            isLiveMode={true}
            selectedImageId={(answer as DragMatchingImageAnswer | undefined)?.matches?.text}
            isFrozen={isFrozen}
          />
        );
      case "oddTextOut":
        return (
          <OddTextOutRenderer
            element={element}
            isLiveMode={true}
            selectedId={(answer as OddOutAnswer | undefined)?.selectedId ?? undefined}
            isFrozen={isFrozen}
          />
        );
      case "oddImageOut":
        return (
          <OddImageOutRenderer
            element={element}
            isLiveMode={true}
            selectedId={(answer as OddOutAnswer | undefined)?.selectedId ?? undefined}
            isFrozen={isFrozen}
          />
        );
      case "fillBlanks":
        return (
          <FillBlanksRenderer
            element={element}
            isLiveMode={true}
            selectedAnswers={(answer as FillBlanksAnswer | undefined)?.answers}
            frozenBlanks={frozenBlanks}
            isFrozen={isFrozen}
            canvasHeight={canvasHeight}
          />
        );
      case "answerImage":
        return <AnswerImageRenderer element={element} isLiveMode={true} />;
      case "orderImage":
        return (
          <OrderImageRenderer
            element={element}
            isLiveMode={true}
            answer={answer as OrderAnswer | undefined}
            isFrozen={isFrozen}
          />
        );
      case "orderText":
        return (
          <OrderTextRenderer
            element={element}
            isLiveMode={true}
            answer={answer as OrderAnswer | undefined}
            isFrozen={isFrozen}
          />
        );
      case "spelling":
        return (
          <SpellingRenderer
            element={element}
            isLiveMode={true}
            answer={answer as SpellingAnswer | undefined}
          />
        );
      case "composition":
        return <CompositionRenderer element={element} isLiveMode={true} />;
      case "speaking":
        return (
          <SpeakingRenderer
            element={element}
            isLiveMode={true}
            answer={answer as SpeakingAnswer | undefined}
            isFrozen={isFrozen}
          />
        );
      case "phonic":
        return <PhonicRenderer element={element} isLiveMode={true} />;
      default:
        return null;
    }
  };

  return (
    <div
      className="absolute"
      style={{
        left: element.position.x,
        top: element.position.y,
        width: element.size.width,
        height: element.size.height,
        zIndex: element.zIndex || 0,
        transform: `rotate(${element.rotation ?? 0}deg)`,
      }}
    >
      <div
        className="w-full h-full relative"
        style={{
          border:
            element.border?.stroke &&
            element.border?.lineStyle !== "none" &&
            element.border?.stroke !== "transparent"
              ? `${element.border.strokeWidth ?? 1}px ${element.border.lineStyle ?? "solid"} ${element.border.stroke}`
              : "none",
          borderRadius: element.border?.cornerRadius ?? 0,
          boxSizing: "border-box",
        }}
      >
        {renderElement()}
      </div>
    </div>
  );
}
